import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../../theme/ThemeContext";
import {
  openOrder,
  getOrderTypeLabel,
  isBuyOrder,
} from "../../services/platform/TradingStore";
import {
  PlusIcon,
  MinusIcon,
  ChevronDownIcon,
  ActivityIcon,
} from "../../helpers/Icons";
import PriceDisplay from "../common/PriceDisplay";
import Spread from "../common/Spread";
import { usePriceSubscription } from "../../hooks/usePriceSubscription";
import { useSelector } from "react-redux";
import { selectSettings } from "../../redux/features/platformSlice";

const ConfirmationModal = ({ title, message, onConfirm, onClose }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
    >
      <div
        style={{
          background: theme.card,
          borderRadius: 12,
          width: "90%",
          maxWidth: 350,
          border: `1px solid ${theme.border}`,
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ padding: 20, textAlign: "center" }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: theme.text,
              marginBottom: 10,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 13, color: theme.textM, lineHeight: 1.5 }}>
            {message}
          </div>
        </div>
        <div
          style={{ display: "flex", borderTop: `1px solid ${theme.border}` }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: 15,
              background: "none",
              border: "none",
              borderRight: `1px solid ${theme.border}`,
              color: theme.textS,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: 15,
              background: "none",
              border: "none",
              color: theme.primary,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const NumInput = ({
  label,
  val,
  onChange,
  placeholder,
  color,
  type = "text",
  onStep,
  field,
  theme,
  point,
  onInitialize,
}) => (
  <div style={{ marginTop: 8 }}>
    <div
      style={{
        fontSize: 10,
        color: theme.textM,
        marginBottom: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{label}</span>
      {onInitialize && field && (
        <span
          onClick={() => onInitialize(field)}
          style={{ cursor: "pointer", color: theme.primaryL, fontSize: 9 }}
        >
          Current
        </span>
      )}
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: theme.card2,
        border: `1px solid ${color || theme.border}`,
        borderRadius: 5,
        overflow: "hidden",
        height: 30,
      }}
    >
      {onStep && (
        <button
          onClick={() => onStep(-point)}
          style={{
            padding: "4px 6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <MinusIcon s={10} c={theme.textS} />
        </button>
      )}
      <input
        type={type}
        value={val}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          padding: "5px 8px",
          color: theme.text,
          fontSize: 12,
          fontFamily: theme.mono,
          outline: "none",
          textAlign: onStep ? "center" : "left",
          width: "100%",
          boxSizing: "border-box",
          height: "100%",
        }}
      />
      {onStep && (
        <button
          onClick={() => onStep(point)}
          style={{
            padding: "4px 6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <PlusIcon s={10} c={theme.textS} />
        </button>
      )}
    </div>
  </div>
);

const getNewYorkMarketTime = () =>
  new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
  );

const isForexMarketClosed = () => {
  const nyTime = getNewYorkMarketTime();
  const day = nyTime.getDay();
  const hour = nyTime.getHours();
  const minute = nyTime.getMinutes();
  const minutes = hour * 60 + minute;
  const fivePm = 17 * 60;

  if (day === 6) return true;
  if (day === 0) return minutes < fivePm;
  if (day === 5) return minutes >= fivePm;
  return false;
};

const TradePanel = ({ symbol, item }) => {
  const theme = useTheme();
  const settings = useSelector(selectSettings);
  const currentPrice = usePriceSubscription(symbol);

  const [tradeType, setTradeType] = useState("market"); // 'market' or 'pending'
  const [orderType, setOrderType] = useState(2); // 2:BuyLimit, 3:SellLimit, 4:BuyStop, 5:SellStop
  const [volume, setVolume] = useState("0.01");
  const [price, setPrice] = useState("");
  const [showSLTP, setShowSLTP] = useState(false);
  const [sl, setSL] = useState("");
  const [tp, setTP] = useState("");
  const [status, setStatus] = useState(null);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [openingType, setOpeningType] = useState(null);

  // ... (previous useEffect and initializeField logic unchanged)
  // I will just use a large block replacement to ensure consistency

  const digits = item?.digits ?? 5;
  const point = Math.pow(10, -digits);
  const pip = point * 10;
  const minGapPips = 40;
  const minGap = minGapPips * pip;

  // Sync initial values when switching trade type or order type
  const initializeField = useCallback(
    (field) => {
      if (!currentPrice) return;
      const bid = currentPrice.bid;
      const ask = currentPrice.ask;
      const offset = (minGapPips + 10) * pip; // Default offset slightly more than min gap

      if (field === "price" && tradeType === "pending") {
        let val = 0;
        if (orderType === 2) val = ask - offset; // Buy Limit
        if (orderType === 3) val = bid + offset; // Sell Limit
        if (orderType === 4) val = ask + offset; // Buy Stop
        if (orderType === 5) val = bid - offset; // Sell Stop
        setPrice(val.toFixed(digits));
      } else if (field === "sl") {
        let val = 0;
        if (tradeType === "market") {
          val = bid - offset;
        } else {
          const p = parseFloat(price) || (isBuyOrder(orderType) ? ask : bid);
          val = isBuyOrder(orderType) ? p - offset : p + offset;
        }
        setSL(val.toFixed(digits));
      } else if (field === "tp") {
        let val = 0;
        if (tradeType === "market") {
          val = ask + offset;
        } else {
          const p = parseFloat(price) || (isBuyOrder(orderType) ? ask : bid);
          val = isBuyOrder(orderType) ? p + offset : p - offset;
        }
        setTP(val.toFixed(digits));
      }
    },
    [currentPrice, tradeType, orderType, price, digits, pip, minGapPips],
  );

  useEffect(() => {
    setPrice("");
    setSL("");
    setTP("");
    setShowSLTP(false);
  }, [tradeType]);

  const adjVol = (delta) => {
    const v = Math.max(
      0.01,
      Math.round((parseFloat(volume || "0.01") + delta) * 100) / 100,
    );
    setVolume(v.toFixed(2));
  };

  const adjValue = (val, setter, delta, field) => {
    let current = parseFloat(val);
    if (isNaN(current)) {
      if (!currentPrice) return;
      const bid = currentPrice.bid;
      const ask = currentPrice.ask;
      const offset = (minGapPips + 10) * pip;
      if (field === "price") {
        if (orderType === 2) current = ask - offset;
        if (orderType === 3) current = bid + offset;
        if (orderType === 4) current = ask + offset;
        if (orderType === 5) current = bid - offset;
      } else if (field === "sl") {
        current = bid - offset;
      } else if (field === "tp") {
        current = ask + offset;
      } else {
        current = bid;
      }
    } else {
      current += delta;
    }
    setter(current.toFixed(digits));
  };

  const validation = useMemo(() => {
    if (!currentPrice) return { ok: false, msg: "Waiting for price..." };
    const bid = currentPrice.bid;
    const ask = currentPrice.ask;

    const v = parseFloat(volume);
    if (isNaN(v) || v <= 0) return { ok: false, msg: "Invalid volume" };

    if (tradeType === "pending") {
      const p = parseFloat(price);
      if (isNaN(p) || p <= 0)
        return { ok: false, msg: "Invalid pending price" };

      if (orderType === 2 && p > ask - minGap)
        return {
          ok: false,
          msg: `Price must be <= ${(ask - minGap).toFixed(digits)}`,
        };
      if (orderType === 3 && p < bid + minGap)
        return {
          ok: false,
          msg: `Price must be >= ${(bid + minGap).toFixed(digits)}`,
        };
      if (orderType === 4 && p < ask + minGap)
        return {
          ok: false,
          msg: `Price must be >= ${(ask + minGap).toFixed(digits)}`,
        };
      if (orderType === 5 && p > bid - minGap)
        return {
          ok: false,
          msg: `Price must be <= ${(bid - minGap).toFixed(digits)}`,
        };

      const isBuy = isBuyOrder(orderType);
      const isSell = !isBuy;

      if (sl) {
        const s = parseFloat(sl);
        if (isBuy && s > p - minGap)
          return {
            ok: false,
            msg: `SL must be <= ${(p - minGap).toFixed(digits)}`,
          };
        if (isSell && s < p + minGap)
          return {
            ok: false,
            msg: `SL must be >= ${(p + minGap).toFixed(digits)}`,
          };
      }
      if (tp) {
        const t = parseFloat(tp);
        if (isBuy && t < p + minGap)
          return {
            ok: false,
            msg: `TP must be >= ${(p + minGap).toFixed(digits)}`,
          };
        if (isSell && t > p - minGap)
          return {
            ok: false,
            msg: `TP must be <= ${(p - minGap).toFixed(digits)}`,
          };
      }
    } else {
      if (sl) {
        const s = parseFloat(sl);
        const validForBuy = s <= ask - minGap;
        const validForSell = s >= bid + minGap;
        if (!validForBuy && !validForSell)
          return { ok: false, msg: "SL too close to price" };
      }
      if (tp) {
        const t = parseFloat(tp);
        const validForBuy = t >= ask + minGap;
        const validForSell = t <= bid - minGap;
        if (!validForBuy && !validForSell)
          return { ok: false, msg: "TP too close to price" };
      }
    }

    return { ok: true };
  }, [
    currentPrice,
    volume,
    tradeType,
    orderType,
    price,
    sl,
    tp,
    minGap,
    digits,
  ]);

  // Market validation per side
  const canBuy =
    tradeType === "market" &&
    validation.ok &&
    (!sl || parseFloat(sl) <= currentPrice?.ask - minGap) &&
    (!tp || parseFloat(tp) >= currentPrice?.ask + minGap);
  const canSell =
    tradeType === "market" &&
    validation.ok &&
    (!sl || parseFloat(sl) >= currentPrice?.bid + minGap) &&
    (!tp || parseFloat(tp) <= currentPrice?.bid - minGap);

  const doTrade = async (type) => {
    if (openingType !== null) return;
    if (settings.confirmOpen) {
      setPendingTrade({ type });
    } else {
      executeTrade(type);
    }
  };

  const executeTrade = async (type) => {
    if (openingType !== null) return;
    setOpeningType(type);
    try {
      const result = await openOrder({
        symbol,
        type,
        volume,
        sl: sl || "0",
        tp: tp || "0",
        price: tradeType === "pending" ? price : undefined,
      });

      if (result.success !== false) {
        const orderId =
          result.order?.ticket ||
          result.order?.order ||
          result.data?.ticket ||
          result.data?.order ||
          result.data?.id;
        setStatus({
          ok: true,
          msg: orderId ? `Order #${orderId} opened` : "Order request sent",
        });
      } else {
        setStatus({ ok: false, msg: result.message || "Error" });
      }
      setPendingTrade(null);
      setTimeout(() => setStatus(null), 3000);
    } finally {
      setOpeningType(null);
    }
  };

  const marketType = String(
    item?.category || item?.market || item?.type || "",
  ).toLowerCase();
  const isCrypto =
    marketType === "crypto" ||
    symbol?.toUpperCase().includes("BTC") ||
    symbol?.toUpperCase().includes("ETH");
  const isMarketClosed = !isCrypto && isForexMarketClosed();
  const canPlacePending = tradeType === "pending" && validation.ok;
  const isOpeningSell = openingType === 1;
  const isOpeningBuy = openingType === 0;
  const isOpeningPending = openingType !== null && tradeType === "pending";

  const PENDING_TYPES = [
    { id: 2, label: "Buy Limit" },
    { id: 3, label: "Sell Limit" },
    { id: 4, label: "Buy Stop" },
    { id: 5, label: "Sell Stop" },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        background: theme.card,
        padding: "8px 10px",
        gap: 6,
        overflow: "hidden",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Symbol header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: theme.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {symbol}
          </div>
          <div
            style={{
              fontSize: 10,
              color: theme.textM,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item?.description ?? ""}
          </div>
        </div>
        {isMarketClosed && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: theme.orange,
              background: theme.orangeBg,
              padding: "2px 7px",
              borderRadius: 4,
            }}
          >
            CLOSED
          </span>
        )}
      </div>

      {/* Trade Type Selector */}
      <div
        style={{
          display: "flex",
          gap: 4,
          background: theme.card2,
          padding: 3,
          borderRadius: 8,
          minWidth: 0,
        }}
      >
        <button
          onClick={() => setTradeType("market")}
          style={{
            flex: "1 1 0",
            minWidth: 0,
            padding: "7px 5px",
            border: "none",
            borderRadius: 6,
            fontSize: 10,
            fontWeight: 700,
            cursor: "pointer",
            background: tradeType === "market" ? theme.card : "transparent",
            color: tradeType === "market" ? theme.primary : theme.textM,
            transition: "all 0.2s",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Market Execution
        </button>
        <button
          onClick={() => setTradeType("pending")}
          style={{
            flex: "1 1 0",
            minWidth: 0,
            padding: "7px 5px",
            border: "none",
            borderRadius: 6,
            fontSize: 10,
            fontWeight: 700,
            cursor: "pointer",
            background: tradeType === "pending" ? theme.card : "transparent",
            color: tradeType === "pending" ? theme.primary : theme.textM,
            transition: "all 0.2s",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Pending Order
        </button>
      </div>

      {/* Bid/Ask (Only show for market) */}
      {tradeType === "market" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0, 1fr) minmax(30px, auto) minmax(0, 1fr)",
            gap: 3,
            alignItems: "center",
            minWidth: 0,
            marginTop: 10,
          }}
        >
          <div
            style={{
              minWidth: 0,
              overflow: "hidden",
              background: theme.negBg,
              borderRadius: 8,
              padding: "8px 6px",
              textAlign: "center",
              border: `1px solid ${theme.neg}22`,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: theme.neg,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 2,
              }}
            >
              Sell
            </div>
            <PriceDisplay
              symbol={symbol}
              type="bid"
              digits={digits}
              style={{
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                fontSize: 14,
                fontWeight: 700,
                color: theme.neg,
                fontFamily: theme.mono,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.15,
              }}
            />
          </div>
          <div
            style={{ minWidth: 30, textAlign: "center", overflow: "hidden" }}
          >
            <div style={{ fontSize: 9, color: theme.textM }}>Spread</div>
            <Spread
              symbol={symbol}
              digits={digits}
              style={{
                display: "block",
                maxWidth: 42,
                overflow: "hidden",
                textOverflow: "clip",
                whiteSpace: "nowrap",
                fontSize: 10,
                color: theme.textS,
                fontFamily: theme.mono,
              }}
            />
          </div>
          <div
            style={{
              minWidth: 0,
              overflow: "hidden",
              background: theme.posBg,
              borderRadius: 8,
              padding: "8px 6px",
              textAlign: "center",
              border: `1px solid ${theme.pos}22`,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: theme.pos,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 2,
              }}
            >
              Buy
            </div>
            <PriceDisplay
              symbol={symbol}
              type="ask"
              digits={digits}
              style={{
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                fontSize: 14,
                fontWeight: 700,
                color: theme.pos,
                fontFamily: theme.mono,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.15,
              }}
            />
          </div>
        </div>
      )}

      {/* Pending Order Type */}
      {tradeType === "pending" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 6,
            minWidth: 0,
            marginTop: 10,
          }}
        >
          {PENDING_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setOrderType(t.id)}
              style={{
                marginTop: 4,
                marginBottom: 4,
                minWidth: 0,
                padding: "8px 6px",
                borderRadius: 6,
                border: `1px solid ${orderType === t.id ? theme.primary : theme.border}`,
                background:
                  orderType === t.id ? theme.primaryGlow : theme.card2,
                color: orderType === t.id ? theme.primaryL : theme.textS,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Volume */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: theme.textM, marginBottom: 4 }}>
          Volume (Lots)
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            width: "100%",
            minWidth: 0,
          }}
        >
          <button
            onClick={() => adjVol(-0.01)}
            style={{
              flex: "0 0 30px",
              width: 30,
              minWidth: 30,
              height: 30,
              minHeight: 30,
              borderRadius: 6,
              background: theme.card2,
              border: `1px solid ${theme.border}`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MinusIcon s={13} c={theme.textS} />
          </button>
          <input
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              width: "100%",
              textAlign: "center",
              background: theme.card2,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              padding: "7px 8px",
              color: theme.text,
              fontSize: 13,
              fontFamily: theme.mono,
              outline: "none",
            }}
          />
          <button
            onClick={() => adjVol(0.01)}
            style={{
              flex: "0 0 30px",
              width: 30,
              minWidth: 30,
              height: 30,
              minHeight: 30,
              borderRadius: 6,
              background: theme.card2,
              border: `1px solid ${theme.border}`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlusIcon s={13} c={theme.textS} />
          </button>
        </div>
      </div>

      {/* Price (Pending only) */}
      {tradeType === "pending" && (
        <NumInput
          label="Price"
          val={price}
          onChange={setPrice}
          placeholder="0.00000"
          color={theme.primary + "44"}
          onStep={(d) => adjValue(price, setPrice, d, "price")}
          field="price"
          theme={theme}
          point={point}
          onInitialize={initializeField}
        />
      )}

      {/* SL/TP toggle & Fields */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          marginTop: 10,
        }}
      >
        <button
          onClick={() => setShowSLTP((p) => !p)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            background: theme.card2,
            border: `1px solid ${theme.border}`,
            borderRadius: 7,
            padding: "6px 10px",
            cursor: "pointer",
            width: "100%",
            minWidth: 0,
          }}
        >
          <span
            style={{
              minWidth: 0,
              fontSize: 11,
              color: theme.textS,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Stop Loss / Take Profit
          </span>
          <ChevronDownIcon
            s={12}
            c={theme.textM}
            style={{
              transform: `rotate(${showSLTP ? 180 : 0}deg)`,
              transition: "transform 0.2s",
            }}
          />
        </button>

        {showSLTP && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <NumInput
              label="Stop Loss"
              val={sl}
              onChange={setSL}
              placeholder="0.00000"
              color={theme.neg + "33"}
              onStep={(d) => adjValue(sl, setSL, d, "sl")}
              field="sl"
              theme={theme}
              point={point}
              onInitialize={initializeField}
            />
            <NumInput
              label="Take Profit"
              val={tp}
              onChange={setTP}
              placeholder="0.00000"
              color={theme.pos + "33"}
              onStep={(d) => adjValue(tp, setTP, d, "tp")}
              field="tp"
              theme={theme}
              point={point}
              onInitialize={initializeField}
            />
          </div>
        )}
      </div>

      {/* Validation Message */}
      {!validation.ok && (
        <div
          style={{
            fontSize: 11,
            color: theme.neg,
            fontWeight: 600,
            padding: "4px 2px",
          }}
        >
          {validation.msg}
        </div>
      )}

      {/* Status */}
      {status && (
        <div
          style={{
            padding: "7px 10px",
            borderRadius: 7,
            background: status.ok ? theme.posBg : theme.negBg,
            border: `1px solid ${status.ok ? theme.pos : theme.neg}44`,
            fontSize: 11,
            color: status.ok ? theme.pos : theme.neg,
            fontWeight: 600,
          }}
        >
          {status.msg}
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Trade buttons */}
      {tradeType === "market" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 8,
            minWidth: 0,
          }}
        >
          <button
            onClick={() => doTrade(1)}
            disabled={isMarketClosed || !canSell || openingType !== null}
            style={{
              minWidth: 0,
              overflow: "hidden",
              padding: "12px 6px",
              borderRadius: 9,
              border: "none",
              cursor:
                isMarketClosed || !canSell || openingType !== null
                  ? "not-allowed"
                  : "pointer",
              background: isMarketClosed || !canSell ? theme.border : theme.neg,
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: theme.sans,
              opacity:
                (!isMarketClosed && canSell && openingType === null) ||
                isOpeningSell
                  ? 1
                  : 0.6,
            }}
          >
            {isOpeningSell ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  minHeight: 34,
                }}
              >
                <ActivityIcon
                  s={14}
                  c="#fff"
                  style={{ animation: "trade-spin 0.9s linear infinite" }}
                />
                <span>Opening</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    opacity: 0.8,
                    marginBottom: 1,
                  }}
                >
                  SELL
                </div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: 14,
                    fontFamily: theme.mono,
                    fontVariantNumeric: "tabular-nums",
                    overflow: "hidden",
                  }}
                >
                  <PriceDisplay
                    symbol={symbol}
                    type="bid"
                    digits={digits}
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      color: "#fff",
                      fontFamily: theme.mono,
                      fontSize: 14,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                </div>
              </>
            )}
          </button>

          <button
            onClick={() => doTrade(0)}
            disabled={isMarketClosed || !canBuy || openingType !== null}
            style={{
              minWidth: 0,
              overflow: "hidden",
              padding: "12px 6px",
              borderRadius: 9,
              border: "none",
              cursor:
                isMarketClosed || !canBuy || openingType !== null
                  ? "not-allowed"
                  : "pointer",
              background: isMarketClosed || !canBuy ? theme.border : theme.pos,
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: theme.sans,
              opacity:
                (!isMarketClosed && canBuy && openingType === null) ||
                isOpeningBuy
                  ? 1
                  : 0.6,
            }}
          >
            {isOpeningBuy ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  minHeight: 34,
                }}
              >
                <ActivityIcon
                  s={14}
                  c="#fff"
                  style={{ animation: "trade-spin 0.9s linear infinite" }}
                />
                <span>Opening</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    opacity: 0.8,
                    marginBottom: 1,
                  }}
                >
                  BUY
                </div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: 14,
                    fontFamily: theme.mono,
                    fontVariantNumeric: "tabular-nums",
                    overflow: "hidden",
                  }}
                >
                  <PriceDisplay
                    symbol={symbol}
                    type="ask"
                    digits={digits}
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      color: "#fff",
                      fontFamily: theme.mono,
                      fontSize: 14,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                </div>
              </>
            )}
          </button>
        </div>
      ) : (
        <button
          onClick={() => doTrade(orderType)}
          disabled={!canPlacePending || openingType !== null}
          style={{
            padding: "14px 8px",
            borderRadius: 9,
            border: "none",
            cursor:
              !canPlacePending || openingType !== null
                ? "not-allowed"
                : "pointer",
            background: !canPlacePending ? theme.border : theme.primary,
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: theme.sans,
            opacity:
              canPlacePending && (openingType === null || isOpeningPending)
                ? 1
                : 0.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
          }}
        >
          {isOpeningPending && (
            <ActivityIcon
              s={14}
              c="#fff"
              style={{ animation: "trade-spin 0.9s linear infinite" }}
            />
          )}
          {isOpeningPending
            ? "Opening"
            : `Place ${PENDING_TYPES.find((t) => t.id === orderType)?.label}`}
        </button>
      )}

      <style>{`
        @keyframes trade-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {pendingTrade && (
        <ConfirmationModal
          title="Confirm Open Order"
          message={`Are you sure you want to open a ${volume} lots ${getOrderTypeLabel(pendingTrade.type)} for ${symbol}?`}
          onConfirm={() => executeTrade(pendingTrade.type)}
          onClose={() => setPendingTrade(null)}
        />
      )}
    </div>
  );
};
export default TradePanel;
